import { useState } from 'react';
import { motion } from 'motion/react';
import { Users, UserPlus, Trash2, Loader2, AlertCircle, ShieldCheck, Search } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button, Card, CardContent, CardHeader, CardTitle, CardDescription,
  Badge, Input, Label, Separator,
} from '@higgins/ui';
import { useUserRoles, useAssignUserRole, useRemoveUserRole, useDirectory } from '@/lib/hooks';
import type { AddedRole, UserRole, UserRoleAssignment } from '@/lib/types';
import type { DirectoryUser } from '@/lib/api';

const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

const MOCK_ASSIGNMENTS: UserRoleAssignment[] = [
  { id: '1', oid: '', upn: 'j.smith@higgins.com.au', displayName: 'Jane Smith', role: 'Builder', assignedByName: 'D Davies', assignedOn: '2026-06-10T09:00:00Z' },
  { id: '2', oid: '', upn: 'a.brown@higgins.com.au', displayName: 'Alan Brown', role: 'Admin', assignedByName: 'D Davies', assignedOn: '2026-06-09T14:30:00Z' },
  { id: '3', oid: '', upn: 'r.jones@higgins.com.au', displayName: 'Riya Jones', role: 'Standard', assignedByName: 'D Davies', assignedOn: '2026-06-11T08:15:00Z' },
];

const MOCK_DIRECTORY: DirectoryUser[] = [
  { name: 'Cameron Sheehan', email: 'csheehan@higgins.com.au', oid: '00000000-0000-0000-0000-000000000001' },
  { name: 'Heidi Butler', email: 'hbutler@higgins.com.au', oid: '00000000-0000-0000-0000-000000000002' },
  { name: 'Scott Kelly', email: 'skelly@higgins.com.au', oid: '00000000-0000-0000-0000-000000000003' },
  { name: 'Troy Selwood', email: 'tselwood@higgins.com.au', oid: '00000000-0000-0000-0000-000000000004' },
];

/** What can be assigned here. Standard comes from the portal; a Standard row left from before #704 adds nothing. */
const ROLES: AddedRole[] = ['Builder', 'Admin'];
const FILTER_ROLES: UserRole[] = ['Builder', 'Admin', 'Standard'];

const ROLE_BADGE: Record<UserRole, string> = {
  Admin: 'bg-orange-100 text-orange-700 border-orange-200',
  Builder: 'bg-purple-100 text-purple-700 border-purple-200',
  Standard: 'bg-slate-100 text-slate-600 border-slate-200',
};

function formatDate(iso?: string) {
  return iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

function AssignmentRow({ a, onRemove, busy }: {
  a: UserRoleAssignment;
  onRemove: (id: string) => void;
  busy: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3 gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{a.displayName}</p>
        <p className="text-xs text-muted-foreground truncate">
          {a.upn}
          {a.assignedByName && ` · assigned by ${a.assignedByName}`}
          {a.assignedOn && ` on ${formatDate(a.assignedOn)}`}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge className={ROLE_BADGE[a.role]}>{a.role}</Badge>
        <Button
          variant="ghost" size="sm" className="h-7 px-2 text-destructive hover:text-destructive"
          onClick={() => onRemove(a.id)}
          disabled={busy}
          title="Remove this role assignment"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const { data: assignments = IS_MOCK ? MOCK_ASSIGNMENTS : [], isLoading, error } = useUserRoles();
  const { data: directory = IS_MOCK ? MOCK_DIRECTORY : [], isLoading: dirLoading } = useDirectory();
  const assignMutation = useAssignUserRole();
  const removeMutation = useRemoveUserRole();

  const [picked, setPicked] = useState<DirectoryUser | null>(null);
  const [dirQuery, setDirQuery] = useState('');
  const [showList, setShowList] = useState(false);
  const [role, setRole] = useState<AddedRole>('Builder');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'All' | UserRole>('All');

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!picked) { toast.error('Select a person', { description: 'Choose someone from the directory list.' }); return; }
    if (IS_MOCK) { toast.info('Assign (mock)', { description: 'Not available in demo mode.' }); return; }
    try {
      const res = await assignMutation.mutateAsync({ upn: picked.email, displayName: picked.name, role, oid: picked.oid });
      toast.success(res.updated ? 'Role updated' : 'Role assigned', { description: `${picked.name} → ${role}` });
      setPicked(null); setDirQuery(''); setShowList(false); setRole('Builder');
    } catch (e: any) {
      toast.error('Assign failed', { description: e?.message });
    }
  }

  async function handleRemove(id: string) {
    if (IS_MOCK) { toast.info('Remove (mock)', { description: 'Not available in demo mode.' }); return; }
    if (!window.confirm('Remove this role? The person keeps whatever the portal gives them (Standard, or Admin for the apps team).')) return;
    try {
      await removeMutation.mutateAsync(id);
      toast.success('Assignment removed');
    } catch (e: any) {
      toast.error('Remove failed', { description: e?.message });
    }
  }

  const dq = dirQuery.trim().toLowerCase();
  const matches = dq
    ? directory.filter(u => u.name.toLowerCase().includes(dq) || u.email.toLowerCase().includes(dq)).slice(0, 8)
    : [];

  const q = search.trim().toLowerCase();
  const filtered = assignments.filter(a =>
    (roleFilter === 'All' || a.role === roleFilter) &&
    (!q || a.displayName.toLowerCase().includes(q) || a.upn.toLowerCase().includes(q)),
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl space-y-6"
    >
      <div>
        <h2 className="text-xl font-bold text-slate-900">User Access</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Who can open the app is set on the Higgins Apps portal, and everyone it lets in is
          <strong> Standard</strong>: they create and edit reports. Here you add more for someone
          already let in: <strong>Builder</strong> adds the combined-report Builder; <strong>Admin</strong>
          adds Templates &amp; User Access. The apps team (HigginsApps-Dev) are Admin automatically.
          A role here never lets in someone the portal doesn't.
        </p>
      </div>

      {/* Assign form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Assign a role</CardTitle>
          <CardDescription>Search for a person, pick Builder or Admin, and assign. Only active, licensed staff appear.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAssign} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="person">Person</Label>
              <div className="relative">
                <Input
                  id="person"
                  autoComplete="off"
                  placeholder={dirLoading ? 'Loading directory…' : 'Search for a person by name or email…'}
                  value={dirQuery}
                  onChange={e => { setDirQuery(e.target.value); setPicked(null); setShowList(true); }}
                  onFocus={() => setShowList(true)}
                  onBlur={() => setTimeout(() => setShowList(false), 150)}
                />
                {showList && dq && matches.length > 0 && (
                  <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-card shadow-md">
                    {matches.map(u => (
                      <li key={u.email}>
                        <button
                          type="button"
                          onMouseDown={() => { setPicked(u); setDirQuery(u.name); setShowList(false); }}
                          className="w-full text-left px-3 py-2 hover:bg-accent"
                        >
                          <span className="text-sm font-medium">{u.name}</span>
                          <span className="block text-xs text-muted-foreground">{u.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {showList && dq && !dirLoading && matches.length === 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card shadow-md px-3 py-2 text-sm text-muted-foreground">
                    No active users match.
                  </div>
                )}
              </div>
              {picked && (
                <p className="text-xs text-muted-foreground">Selected <strong>{picked.name}</strong> — {picked.email}</p>
              )}
            </div>
            <div className="flex items-end gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  value={role}
                  onChange={e => setRole(e.target.value as AddedRole)}
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <Button type="submit" disabled={assignMutation.isPending}>
                {assignMutation.isPending
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <UserPlus className="w-4 h-4 mr-1" />}
                Assign
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Users with access */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" /> Builders and Admins
            {!isLoading && assignments.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                ({filtered.length}{filtered.length !== assignments.length ? ` of ${assignments.length}` : ''})
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Everyone given Builder or Admin here. The apps team are Admin through HigginsApps-Dev
            and aren't listed. A Standard row adds nothing and can be removed.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Search + role filter */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as 'All' | UserRole)}
              className="h-9 rounded-md border border-border bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option value="All">All roles</option>
              {FILTER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 py-4 text-destructive text-sm">
              <AlertCircle className="w-4 h-4" /> Failed to load assignments
            </div>
          )}
          {!isLoading && assignments.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground text-sm">
              <ShieldCheck className="w-5 h-5" />
              No users have been granted access yet.
            </div>
          )}
          {!isLoading && assignments.length > 0 && filtered.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">No users match your search.</p>
          )}
          {!isLoading && filtered.map((a, i) => (
            <div key={a.id}>
              {i > 0 && <Separator />}
              <AssignmentRow a={a} onRemove={handleRemove} busy={removeMutation.isPending} />
            </div>
          ))}
        </CardContent>
      </Card>
    </motion.div>
  );
}
